import axios from 'axios';
import useI18n from "./useI18n";
import { useNavigate } from "react-router-dom";
import useEnv from "./useEnv";
import { jwtDecode } from "jwt-decode";

const ERR_TOKEN_EXPIRED = 'ERR_TOKEN_EXPIRED';
const RETRYABLE_METHODS = new Set(['get', 'head']);
const RETRYABLE_STATUSES = new Set([502, 503, 504]);
const RETRYABLE_CODES = new Set(['ERR_NETWORK', 'ECONNABORTED', 'ETIMEDOUT']);
const RETRY_BASE_DELAY_MS = 200;

/**
 * Hook providing the HTTP client for the Ropo24 REST API.
 *
 * Wraps an axios instance (base URL from `VITE_API_URL`) and registers interceptors that handle
 * cross-cutting concerns, so callers only deal with the request and its data:
 * - Request interceptor: adds the `Authorization: Bearer` header from sessionStorage, and aborts
 *   the request without sending it if the JWT has expired.
 * - Response interceptor: signs the user out on 401 (or expired token) and retries idempotent
 *   requests on transient failures (see `VITE_API_RETRIES`, `VITE_API_TIMEOUT`).
 *
 * Methods resolve to `null` when the user was signed out; all other errors are thrown to the caller.
 * Use this instead of raw axios so auth, locale and error handling stay consistent.
 *
 * @returns {{
 *   getJson: function(string): Promise<any|null>,
 *   getBlob: function(string, string): Promise<any|null>,
 *   postJson: function(string, any): Promise<any|null>,
 *   putJson: function(string, any): Promise<any|null>,
 *   patchJson: function(string, any): Promise<any|null>,
 *   deleteJson: function(string): Promise<any|null>
 * }}
 */
const useApi = () => {
    const navigate = useNavigate();
    const { getLocaleBCP47 } = useI18n();
    const { getEnv, getEnvInt, isDev } = useEnv();

    /**
     * Checks whether the token has expired (exp claim). Undecodable tokens or tokens
     * without exp are not treated as expired; the back-end remains the authority for those.
     *
     * @param {string|null} token
     * @returns {boolean}
     */
    const isTokenExpired = (token) => {
        if (token === null || token === '') {
            return false;
        }
        try {
            const { exp } = jwtDecode(token);
            return typeof exp === 'number' && exp * 1000 <= Date.now();
        } catch (err) {
            // Never log the token itself, only the decode error
            if (isDev) {
                console.log('Unable to decode JWT, treating it as not expired: ', err);
            }
            return false;
        }
    }

    const retries = getEnvInt('API_RETRIES', 0);
    const timeout = getEnvInt('API_TIMEOUT', 0);

    const apiClient = axios.create({
        baseURL: getEnv('API_URL'),
        ...(timeout > 0 && { timeout })
    });

    /**
     * Navigates to the logout page with an authentication error alert. The logout page is
     * responsible for actually clearing the session (token, Cognito, context data).
     *
     * @returns {void}
     */
    const redirectToLogout = () => {
        navigate({ pathname: "/logout", search: "?alert=authError" });
    }

    /**
     * Decides whether a failed request should be sent again. Only idempotent methods (GET/HEAD)
     * are retried so that writes (payments, mandates, servings...) can never be submitted twice.
     * Retries happen on transient failures only: no response (network error, timeout) or
     * 502/503/504. Client errors and 500 are not retried.
     *
     * @param {import('axios').AxiosError} error - The failed request's error
     * @returns {boolean} true if the request should be retried
     */
    const shouldRetry = (error) => {
        const config = error.config;
        if (retries <= 0 || !config || !RETRYABLE_METHODS.has(config.method?.toLowerCase())) {
            return false;
        }
        if ((config.__retryCount ?? 0) >= retries) {
            return false;
        }
        return error.response
            ? RETRYABLE_STATUSES.has(error.response.status)
            : RETRYABLE_CODES.has(error.code);
    }

    /**
     * Request interceptor. Reads the token from sessionStorage and adds it as a Bearer token.
     * If the token has already expired the request is rejected with `ERR_TOKEN_EXPIRED` before
     * anything is sent, since the back-end would reject it with 401 anyway. The response
     * interceptor then signs the user out.
     */
    apiClient.interceptors.request.use((config) => {
        const token = sessionStorage.getItem('token');

        if (isTokenExpired(token)) {
            const error = new axios.AxiosError('Token expired', ERR_TOKEN_EXPIRED, config);
            throw error;
        }
        if (token !== null && token !== '') {
            config.headers.set('Authorization', `Bearer ${token}`);
        }
        return config;
    });

    /**
     * Response error interceptor. In order:
     * 1. Expired token or 401: sign the user out, flag the error as handled (so the public
     *    methods resolve to null) and do not retry.
     * 2. Retryable failure (see `shouldRetry`): wait with exponential backoff
     *    (2^n * 200 ms) and re-send the request. The re-sent request passes through the request
     *    interceptor again, so token expiry is re-checked on every attempt.
     * 3. Anything else: reject unchanged so the caller can handle it.
     */
    apiClient.interceptors.response.use((response) => response, async (error) => {
        const status = error.response?.status ?? error.status;

        if (error.code === ERR_TOKEN_EXPIRED || status === 401) {
            // Session is no longer valid: sign out (the logout page clears the session)
            redirectToLogout();
            error.handled = true;
            throw error;
        }

        // Only idempotent requests are retried, so writes can never be submitted twice
        if (shouldRetry(error)) {
            const config = error.config;
            config.__retryCount = (config.__retryCount ?? 0) + 1;
            await new Promise((resolve) => setTimeout(resolve, 2 ** config.__retryCount * RETRY_BASE_DELAY_MS));
            return apiClient(config);
        }

        throw error;
    });

    /**
     * Runs the request. Errors already handled by the interceptors (sign out) resolve to null,
     * other errors are passed forward to the caller.
     *
     * @param {function(): Promise<import('axios').AxiosResponse>} request - Function that fires the request
     * @returns {Promise<import('axios').AxiosResponse|null>} The response, or null if the user was signed out
     */
    const send = async (request) => {
        try {
            return await request();
        } catch (error) {
            if (error.handled) {
                return null;
            }
            throw error;
        }
    }

    /**
     * Fetches JSON data with a GET request. Sends the current locale as `Accept-Language`.
     * Retried on transient failures if `VITE_API_RETRIES` is set.
     *
     * @param {string} url - The URL to fetch data from
     * @returns {Promise<any|null>} The JSON response data, or null if the user was signed out
     */
    const getJson = async (url) => {
        const headers = jsonHeaders({ 'Accept-Language': getLocaleBCP47().replace('_', '-') });
        const response = await send(() => apiClient.get(url, { headers, responseType: 'json' }));
        return response ? response.data : null;
    }

    /**
     * Fetches binary file data (e.g. a PDF) with a GET request.
     * Retried on transient failures if `VITE_API_RETRIES` is set.
     *
     * @param {string} url - The URL to fetch data from
     * @param {string} acceptedMimeType - The MIME type to accept, sent as the `Accept` header
     * @returns {Promise<Blob|null>} The file as a Blob; null if the server answers 204 No Content
     *   or the user was signed out
     */
    const getBlob = async (url, acceptedMimeType) => {
        const headers = jsonHeaders({ 'Accept': acceptedMimeType });
        const response = await send(() => apiClient.get(url, { headers, responseType: 'blob' }));
        return !response || response.status === 204 ? null : response.data;
    }

    /**
     * Posts JSON data with a POST request. Never retried.
     *
     * @param {string} url - The URL to send the data to
     * @param {any} data - The request body, serialized as JSON
     * @returns {Promise<any|null>} The JSON response data, or null if the user was signed out
     */
    const postJson = async (url, data) => {
        const headers = jsonHeaders();
        const response = await send(() => apiClient.post(url, data, { headers, responseType: 'json' }));
        return response ? response.data : null;
    }

    /**
     * Puts JSON data with a PUT request. Never retried.
     *
     * @param {string} url - The URL to send the data to
     * @param {any} data - The request body, serialized as JSON
     * @returns {Promise<any|null>} The JSON response data, or null if the user was signed out
     */
    const putJson = async (url, data) => {
        const headers = jsonHeaders();
        const response = await send(() => apiClient.put(url, data, { headers, responseType: 'json' }));
        return response ? response.data : null;
    }

    /**
     * Patches JSON data with a PATCH request. Never retried.
     *
     * @param {string} url - The URL to send the data to
     * @param {any} data - The request body, serialized as JSON
     * @returns {Promise<any|null>} The JSON response data, or null if the user was signed out
     */
    const patchJson = async (url, data) => {
        const headers = jsonHeaders();
        const response = await send(() => apiClient.patch(url, data, { headers, responseType: 'json' }));
        return response ? response.data : null;
    }

    /**
     * Deletes a resource with a DELETE request. Never retried.
     *
     * @param {string} url - The URL of the resource to delete
     * @returns {Promise<any|null>} The JSON response data, or null if the user was signed out
     */
    const deleteJson = async (url) => {
        const headers = jsonHeaders();
        const response = await send(() => apiClient.delete(url, { headers, responseType: 'json' }));
        return response ? response.data : null;
    }

    /**
     * Base headers for JSON requests. The Authorization header is added by the request interceptor.
     *
     * @param {Object<string, string>} [overrides] - Headers to add or replace, e.g. `{ 'Accept': 'application/pdf' }`
     * @returns {Object<string, string>}
     */
    const jsonHeaders = (overrides = {}) => ({
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        ...overrides
    });

    return {
        getJson,
        getBlob,
        postJson,
        putJson,
        patchJson,
        deleteJson
    }
}

export default useApi
